import React, { useState, useEffect, useCallback } from 'react';
import ListLoadErrorBanner from '../../components/ui/ListLoadErrorBanner';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/ui/Layout';
import Table from '../../components/ui/Table';
import ActionDropdown from '../../components/ui/ActionDropdown';
import ActionButtonsRow from '../../components/ui/ActionButtonsRow';
import { getSimStatusLabel } from '../../utils/simHelpers';
import SearchFilterCard, { FilterConfig } from '../../components/ui/SearchFilterCard';
import BulkActionsBar from '../../components/ui/BulkActionsBar';
import simService, { Sim, SimType, SimStatus, GetSimsParams } from '../../services/simService';
import userService from '../../services/userService';
import { showAlert, showConfirm } from '../../utils/alert';
import { getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import { RiSimCard2Line, RiEyeLine, RiEditLine, RiDeleteBinLine } from 'react-icons/ri';
import { queryKeys } from '../../queryClient';
import { useDeleteSim } from '../../hooks/mutations/useSimMutations';
import { PAGINATION_CONFIG } from '../../config/pagination';
import Pagination from '../../components/ui/Pagination';
import { useUserPageSize } from '../../hooks/useUserPageSize';
import { formatDateTimeFull, getLocalDateString } from '../../utils/dateUtils';
import { downloadExcelFile } from '../../utils/downloadExcel';
import { usePermission } from '../../hooks/usePermission';
import LoadingOverlayModal from '../../components/ui/LoadingOverlayModal';
import { useClampPage } from '../../hooks/useClampPage';
import { runBulkDelete, bulkDeleteFailureMessage } from '../../utils/bulkDelete';
import { detailNavState } from '../../components/detail';
import { SIM_NEW_PATH, simDetailUrl, simEditUrl } from '../../utils/deviceLinks';
import { SIM_OPERATOR_OPTIONS } from '../../components/sim/simForm';

/**
 * หน้ารายการซิมการ์ด — ดู/เพิ่ม/แก้ไขเป็นหน้าเต็มตั้งแต่ 28 ต.ค. 2026 (มติ PO · เดิมเป็นโมดัลในหน้านี้)
 *   ดู → /master-data/sims/:id · แก้ไข → /master-data/sims/:id/edit · เพิ่ม → /master-data/sims/new
 * ลิงก์เก่า ?view=<id> / ?edit=<id> → App.tsx ครอบด้วย LegacyDetailRedirect · ลบยังเป็น confirm ในหน้านี้
 * กติกาฟอร์ม (ค่ายมือถือ ฯลฯ) ย้ายไป components/sim/simForm.ts — re-export ไว้ให้ผู้เรียกเดิม
 */
export { SIM_OPERATOR_OPTIONS, getSimOperatorOptions, isSimOperatorRequired } from '../../components/sim/simForm';

/**
 * ป้ายเตือน "ของยังไม่กลับ" ฝั่งซิม — คู่ขนานของ `PendingReturnBadge` ใน `DevicePage.tsx`
 * (Lane D, 9 ก.ย. 2026)
 *
 * ด่าน L2 ปฏิเสธ **ทั้งเครื่องและซิม** ที่ยังมีใบตามเก็บเปิดค้าง แต่เดิมฝั่งซิมไม่มีอะไรบอกเลย —
 * ซิมหายจาก picker เงียบ ๆ (หลังงานนี้) หรือแย่กว่านั้นคือ **เลือกได้แล้วโดน 400 ตอนกดบันทึก**
 * (ก่อนงานนี้ · prod: AVAILABLE 16 · INACTIVE 8) ผู้ใช้จึงต้องมีที่ที่เห็นเหตุผลได้
 *
 * **เป็นป้าย "เสริม" ไม่ใช่ "แทนที่" สถานะเดิม** — เหตุผลเดียวกับฝั่งเครื่อง: ถ้าไปแทนที่คำว่า
 * "พร้อมใช้งาน" จะครอบแค่กลุ่ม AVAILABLE และพลาดกลุ่มที่อันตรายกว่า (INACTIVE 8 · IN_USE 13
 * ที่ซิมชิ้นเดียวถูกนับอยู่สองที่)
 */
const SimPendingReturnBadge: React.FC<{ record: Sim }> = ({ record }) => {
  if (!record.has_pending_return) return null;
  const n = record.pending_return_count || 0;
  return (
    <div
      className="mt-0.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium bg-warning-100 text-warning-800 border border-warning-200"
      title={
        `ยังมีรายการตามเก็บที่ยังไม่ปิด ${n} รายการ — ซิมอาจยังอยู่กับลูกค้า · ` +
        'ระบบจะไม่ให้นำซิมนี้ไปทำสัญญาใหม่จนกว่าจะบันทึกรับคืน หรือยกเลิกรายการตามเก็บ'
      }
    >
      ⚠ รอรับคืน{n > 1 ? ` (${n})` : ''}
    </div>
  );
};

/**
 * ป้าย "🔒 ผูกกับสัญญา …" — fix ค (20 ก.ย. 2026): ซิมที่ขึ้นว่า "พร้อมใช้งาน" แต่ยังถูกสัญญาที่
 * ยังไม่จบ (DRAFT/ACTIVE/PENDING_CANCELLATION) ผูกอยู่ — backend ส่งเหตุผล + ทางออกมาให้ครบแล้ว
 * (`rental_hold.message`) ที่นี่แค่แสดงผล ห้าม derive เงื่อนไขเอง (ดู utils/rentalHold.ts)
 *
 * คู่ขนานกับ `SimPendingReturnBadge` แต่คนละสี (info ไม่ใช่ warning) และคนละความหมาย:
 * "รอรับคืน" = ของอาจยังอยู่กับลูกค้าจริง ๆ (ยังไม่ยืนยัน) ส่วน "ผูกกับสัญญา" = ระบบยืนยันแล้วว่า
 * สัญญาไหนยังถือของชิ้นนี้อยู่ (fix ก) และมีทางปลดชัดเจน (ปิดใบแจ้งยกเลิกที่หน้าสัญญานั้น)
 */
const SimRentalHoldBadge: React.FC<{ record: Sim }> = ({ record }) => {
  const hold = record.rental_hold;
  if (!hold) return null;
  return (
    <Link
      to={`/rentals/view/${hold.rental_id}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="mt-0.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium bg-info-100 text-info-800 border border-info-200 hover:bg-info-200"
      title={hold.message}
    >
      🔒 ผูกกับสัญญา {hold.rental_number || `#${hold.rental_id}`}
      {hold.goods_returned ? ' · รับคืนแล้ว' : ''}
    </Link>
  );
};

const SimPage: React.FC = () => {
  const { canCreate, canEdit, canDelete, canExport } = usePermission();
  // S1118 URL-sync: read URL params for lazy init (filter+page bidirectional)
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPageParam = searchParams.get('page');
  const urlOperatorParam = searchParams.get('operator') || '';
  const urlTypeParam = searchParams.get('type') || '';
  const urlStatusParam = searchParams.get('status') || '';
  const urlExpiringParam = searchParams.get('expiring') || '';

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState<number>(() => {
    if (!urlPageParam) return 1;
    const n = parseInt(urlPageParam, 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, 9999);
  });
  const [pageSize, setPageSize] = useState(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  useUserPageSize(pageSize, setPageSize);

  // Filter states
  // S1118 URL-sync: hydrate filters from URL (length-bounded for enum-shaped strings, backend validates)
  const [filterOperator, setFilterOperator] = useState<string>(
    urlOperatorParam.length > 0 && urlOperatorParam.length <= 30 ? urlOperatorParam : ''
  );
  const [filterType, setFilterType] = useState<string>(
    urlTypeParam.length > 0 && urlTypeParam.length <= 30 ? urlTypeParam : ''
  );
  const [filterStatus, setFilterStatus] = useState<string>(
    urlStatusParam.length > 0 && urlStatusParam.length <= 30 ? urlStatusParam : ''
  );
  const [showExpiringOnly, setShowExpiringOnly] = useState<boolean>(urlExpiringParam === 'true');

  // S1118 URL-sync: write filter+page to URL (Variant A overwrite — 5-field with inline page reset)
  const syncFiltersToUrl = useCallback((operator: string, type: string, status: string, expiring: boolean, page: number) => {
    const newParams = new URLSearchParams();
    if (operator) newParams.set('operator', operator);
    if (type) newParams.set('type', type);
    if (status) newParams.set('status', status);
    if (expiring) newParams.set('expiring', 'true');
    if (page > 1) newParams.set('page', String(page));
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    syncFiltersToUrl(filterOperator, filterType, filterStatus, showExpiringOnly, currentPage);
  }, [filterOperator, filterType, filterStatus, showExpiringOnly, currentPage, syncFiltersToUrl]);
  
  const navigate = useNavigate();
  const location = useLocation();
  const deleteSimMutation = useDeleteSim();

  // ตัวกรองชุดเดียวของรายการและไฟล์ Excel (CLAUDE.md กติกา 9) — E2E R1 29 ต.ค. 2026: เดิมปุ่มส่งออก
  // เรียก exportToExcel() เปล่า ๆ → ได้ซิมทั้งระบบเสมอ ไม่ว่าจอกรองอะไรอยู่
  const buildFilterParams = (): GetSimsParams => {
    const params: GetSimsParams = {};
    if (searchTerm) params.search = searchTerm;
    if (filterOperator) params.operator = filterOperator;
    if (filterType) params.sim_type = filterType as SimType;
    if (filterStatus) params.status = filterStatus as SimStatus;
    if (showExpiringOnly) {
      params.expiry_soon = true;
      params.days_to_expiry = 30;
    }
    return params;
  };

  // React Query สำหรับดึงข้อมูล
  const { data: simsData, isLoading: simsLoading, isFetching: simsFetching, error: simsError, refetch } = useQuery({
    queryKey: [...queryKeys.sims.list(), {
      page: currentPage, 
      search: searchTerm,
      limit: pageSize,
      operator: filterOperator,
      sim_type: filterType,
      status: filterStatus,
      expiring: showExpiringOnly
    }],
    queryFn: async () => {
      const params: any = { page: currentPage, limit: pageSize, ...buildFilterParams() };
      return await simService.getSims(params);
    },
  });

  // Query สำหรับดึงข้อมูลผู้ใช้ที่ active เท่านั้น (สำหรับ dropdown "ผู้ลงทะเบียน")
  // OBS-S1362-A FIX: use the lightweight /users/performers picker, not the heavy
  // /users/ list (gated by users.view). SimPage is reachable by sims.create roles
  // (Inventory, Support Team) that lack users.view → the old call 403'd and the
  // registrant dropdown was always empty. getPerformers() returns the array of
  // id/full_name/username directly under the broader maintenance.view gate.
  // E2E R1 (29 ต.ค. 2026): endpoint รับ sims.view แล้ว (PERFORMER_PICKER_PERMISSIONS) — ไม่ต้อง gate ฝั่งนี้
  // คอลัมน์ผู้ลงทะเบียนยัง fallback เป็น registration_name เมื่อหา user ไม่เจอ (บัญชีถูกปิด)
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users', 'performers'],
    queryFn: () => userService.getPerformers(),
  });


  // ข้อมูลจาก React Query
  const sims = simsData?.items || [];
  const users = usersData || [];  // OBS-S1362-A: getPerformers() returns the array directly
  const totalItems = simsData?.total || 0;
  useClampPage(currentPage, simsData?.total, pageSize, setCurrentPage, simsLoading);
  const loading = simsLoading || usersLoading || deleteSimMutation.isPending;

  const [selectedSims, setSelectedSims] = useState<number[]>([]);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);

  // ดู/แก้ไข/เพิ่ม = หน้าเต็ม (พก URL รายการไปใน state.from — กดกลับแล้วตัวกรองไม่หาย)
  const handleAdd = () => navigate(SIM_NEW_PATH, { state: detailNavState(location) });
  const handleView = (sim: Sim) => navigate(simDetailUrl(sim.id), { state: detailNavState(location) });
  const handleEdit = (sim: Sim) => navigate(simEditUrl(sim.id), { state: detailNavState(location) });

  const handleDelete = (sim: Sim) => {
    showConfirm(
      'ยืนยันการลบ',
      `คุณต้องการลบซิมการ์ด ${sim.phone_number} ใช่หรือไม่?`,
      async () => {
        deleteSimMutation.mutate(sim.id);
      },
      { isDanger: true }
    );
  };

  const getExpiryStatus = (expiry_date?: string): {status: string; daysLeft: number} => {
    if (!expiry_date) return { status: 'valid', daysLeft: 0 };
    
    const today = new Date();
    const expiryDate = new Date(expiry_date);
    const diffTime = expiryDate.getTime() - today.getTime();
    const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) {
      return { status: 'expired', daysLeft };
    } else if (daysLeft < 30) {
      return { status: 'expiring', daysLeft };
    } else {
      return { status: 'valid', daysLeft };
    }
  };

  // ฟังก์ชันสำหรับการเลือกซิมทั้งหมด
  // KD3 S2515: เปลี่ยนตัวกรอง/หน้าแล้วต้องล้างการเลือก — ไม่งั้นแถบ "เลือกแล้ว N" ค้าง และ "ลบที่เลือก" ลบแถวที่ผู้ใช้มองไม่เห็นแล้ว
  useEffect(() => {
    setSelectedSims([]);
  }, [currentPage, pageSize, searchTerm, filterOperator, filterType, filterStatus]);

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedSims(sims.map(sim => sim.id));
    } else {
      setSelectedSims([]);
    }
  };
  
  // ฟังก์ชันสำหรับการเลือกซิมแต่ละรายการ
  const handleSelectSim = (simId: number, selected: boolean) => {
    if (selected) {
      setSelectedSims(prev => [...prev, simId]);
    } else {
      setSelectedSims(prev => prev.filter(id => id !== simId));
    }
  };
  
  // ฟังก์ชันสำหรับการลบซิมหลายรายการ
  const handleBulkDelete = async () => {
    if (selectedSims.length === 0) {
      showAlert('เลือกข้อมูล', 'กรุณาเลือกซิมที่ต้องการลบ', 'info');
      return;
    }
    
    showConfirm(
      'ยืนยันการลบซิม',
      `คุณต้องการลบซิม ${selectedSims.length} รายการที่เลือก ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้`,
      async () => {
        setBulkLoading(true);
        try {
          // BUG-S2103-002: allSettled — รายการที่ลบไม่ได้ต้องบอกเหตุผล · ตารางต้อง refetch · เลือกค้างเฉพาะที่ลบไม่ได้
          const result = await runBulkDelete(selectedSims, id => simService.deleteSim(id));
          setSelectedSims(result.failed.map(f => f.id));
          refetch();
          if (result.failed.length === 0) {
            showAlert('สำเร็จ', `ลบซิม ${result.succeeded.length} รายการสำเร็จ`, 'success');
          } else {
            showAlert(result.succeeded.length ? 'ลบได้บางรายการ' : 'ลบไม่สำเร็จ', bulkDeleteFailureMessage('ซิม', result), result.succeeded.length ? 'warning' : 'error');
          }
        } catch (error) {
          console.error('Error bulk deleting:', error);
          showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถลบรายการที่เลือกได้', 'error');
        } finally {
          setBulkLoading(false);
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  // Table columns configuration
  const columns = [
    {
      key: 'select',
      title: (
        <div className="flex items-center">
          <input
            type="checkbox"
            aria-label="เลือกซิมทั้งหมด"
            checked={selectedSims.length === sims.length && sims.length > 0}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
          />
        </div>
      ),
      width: '50px',
      render: (value: any, record: Sim) => (
        <input
          type="checkbox"
          aria-label={`เลือกซิม ${record.phone_number || record.id}`}
          checked={selectedSims.includes(record.id)}
          onChange={(e) => handleSelectSim(record.id, e.target.checked)}
          className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
        />
      ),
    },
    {
      key: 'code',
      title: 'รหัสซิม',
      sortable: true,
      render: (value: string, record: Sim) => (
        <Link
          to={simDetailUrl(record.id)}
          state={detailNavState(location)}
          className="font-mono text-sm bg-secondary-100 px-2 py-1 rounded text-primary-700 hover:underline"
          data-testid="sim-code-link"
        >
          {value || `#${record.id}`}
        </Link>
      ),
    },
    {
      key: 'phone_number',
      title: 'เบอร์โทรศัพท์',
      sortable: true,
    },
    {
      key: 'operator',
      title: 'ค่ายมือถือ',
      sortable: true,
    },
    {
      key: 'sim_type',
      title: 'ประเภท',
      sortable: true,
      render: (value: SimType) => {
        switch (value) {
          case SimType.PREPAID: return 'เติมเงิน';
          case SimType.POSTPAID: return 'รายเดือน';
          case SimType.YEARLY: return 'รายปี';
          case SimType.CORPORATE: return 'องค์กร';
          default: return 'อื่นๆ';
        }
      },
    },
    {
      key: 'expiry_date',
      title: 'วันหมดอายุ',
      sortable: true,
      render: (value: string | null, row: Sim) => {
        if (!value) return <span className="text-secondary-400">-</span>;

        const expiryStatus = getExpiryStatus(value);
        const date = new Date(value);

        return (
          <div className={
            expiryStatus.status === 'expired' ? 'text-danger-600' :
            expiryStatus.status === 'expiring' ? 'text-warning-600' :
            'text-secondary-500'
          }>
            {formatDateTimeFull(value)}
            {expiryStatus.status !== 'valid' && (
              <div className="text-xs">
                {expiryStatus.status === 'expired' 
                  ? `หมดอายุแล้ว ${Math.abs(expiryStatus.daysLeft)} วัน`
                  : `ใกล้หมดอายุ (อีก ${expiryStatus.daysLeft} วัน)`
                }
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'registered_by_user_id',
      title: 'ผู้ลงทะเบียน',
      sortable: true,
      render: (value: number | null, row: Sim) => {
        if (!value) return row.registration_name || '-';
        const user = users.find(u => u.id === value);
        return user ? (user.full_name || (user as any).name || user.username) : (row.registration_name || '-');
      },
    },
    {
      key: 'status',
      title: 'สถานะ',
      sortable: true,
      render: (value: SimStatus, row: Sim) => (
        <div>
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value === SimStatus.AVAILABLE ? 'bg-success-100 text-success-800' :
          value === SimStatus.IN_USE ? 'bg-primary-100 text-primary-800' :
          value === SimStatus.ACTIVE ? 'bg-success-100 text-success-800' :
          value === SimStatus.INACTIVE ? 'bg-secondary-100 text-secondary-800' :
          value === SimStatus.SUSPENDED ? 'bg-warning-100 text-warning-800' :
          // ใบงานเตรียมเครื่อง (PO 2026-09-08) — โทน accent เดียวกับป้าย RESERVED ของอุปกรณ์
          value === SimStatus.RESERVED ? 'bg-accent-100 text-accent-800' :
          // ยืมซิม (23 ก.ย. 2026) — สีม่วงเดียวกับอุปกรณ์ ISSUED (designSystem.ts) ไม่ใช่แดง
          value === SimStatus.ISSUED ? 'bg-purple-100 text-purple-700' :
          'bg-danger-100 text-danger-800'
        }`}>
          {/* ใช้ getSimStatusLabel เพื่อไม่ให้ค่าใหม่ที่เพิ่มทีหลังกลายเป็นป้ายเปล่า —
              ของเดิมเป็นชุด `&&` ที่ไม่มี fallback: สถานะที่ไม่ตรงเงื่อนไขไหนเลย (เช่น RESERVED)
              จะ render เป็นวงกลมสีแดง "ว่างเปล่า" ไม่มีตัวอักษร */}
          {getSimStatusLabel(value)}
        </span>
        {/* ยืมซิม: บอกว่าอยู่กับใคร ตามใบเบิกไหน — pattern เดียวกับ DevicePage */}
        {value === SimStatus.ISSUED && row.issued_to_display && (
          <div
            className="text-xs text-secondary-500 mt-0.5 max-w-[10rem] truncate"
            title={
              row.current_stock_issue_number
                ? `${row.issued_to_display} · ใบเบิก ${row.current_stock_issue_number}`
                : row.issued_to_display
            }
          >
            ผู้ยืม: {row.issued_to_display}
            {row.current_stock_issue_number && (
              <span className="block">{row.current_stock_issue_number}</span>
            )}
          </div>
        )}
        {/* Lane D: ป้ายเสริม — ใบตามเก็บที่ยังไม่ปิด (ด่าน L2 กันซิมนี้จากสัญญาใหม่) */}
        <SimPendingReturnBadge record={row} />
        {/* fix ค: ป้ายเสริม — ยังผูกกับสัญญาที่ยังไม่จบอยู่ แม้สถานะจะขึ้นว่าพร้อมใช้งาน */}
        <SimRentalHoldBadge record={row} />
        </div>
      ),
    },
    {
      key: 'created_at',
      title: 'วันที่สร้าง',
      sortable: true,
      render: (value: string | null) => formatDateTimeFull(value)
    },
    {
      key: 'updated_at',
      title: 'อัพเดทล่าสุด',
      sortable: true,
      render: (value: string | null) => formatDateTimeFull(value)
    },
    {
      key: 'actions',
      title: 'การดำเนินการ',
      sticky: true,
      render: (value: any, row: Sim) => (
        <ActionDropdown
          actions={[
            {
              label: 'ดูรายละเอียด',
              icon: <RiEyeLine className="w-4 h-4" />,
              onClick: () => handleView(row)
            },
            canEdit('sims') ? {
              label: 'แก้ไข',
              icon: <RiEditLine className="w-4 h-4" />,
              onClick: () => handleEdit(row)
            } : null,
            (canEdit('sims') || canDelete('sims')) ? {
              divider: true
            } : null,
            canDelete('sims') ? {
              label: 'ลบ',
              icon: <RiDeleteBinLine className="w-4 h-4" />,
              onClick: () => handleDelete(row),
              className: 'text-danger-600 hover:text-danger-700 hover:bg-danger-50'
            } : null
          ].filter(Boolean)}
          buttonIcon={null}
          buttonLabel=""
        />
      ),
    },
  ];

  // ฟังก์ชัน Refresh
  const handleRefresh = async () => {
    setSearchTerm('');
    setFilterOperator('');
    setFilterType('');
    setFilterStatus('');
    setShowExpiringOnly(false);
    setCurrentPage(1);
    setSelectedSims([]);
    await refetch();
  };

  // State สำหรับ Export Loading
  const [isExporting, setIsExporting] = useState(false);

  // ฟังก์ชัน Export Excel
  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const blob = await simService.exportToExcel(buildFilterParams());
      downloadExcelFile(blob, `sims_${getLocalDateString()}.xlsx`);
      showAlert('สำเร็จ', 'ส่งออกข้อมูลเรียบร้อยแล้ว', 'success');
    } catch (error) {
      console.error('Error exporting data:', error);
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถส่งออกข้อมูลได้'), 'error');
    } finally {
      setIsExporting(false);
    }
  };

  // สร้าง filters config สำหรับ SearchFilterCard
  const filters: FilterConfig[] = [
    {
      key: 'operator',
      label: 'ค่ายมือถือ',
      type: 'select',
      tagColor: 'primary',
      // B-C9-2: ชุดเดียวกับฟอร์ม — เดิมตก "อื่นๆ" ทำให้ซิมค่าย OTHER กรองหาไม่เจอ
      options: [...SIM_OPERATOR_OPTIONS]
    },
    {
      key: 'sim_type',
      label: 'ประเภทซิม',
      type: 'select',
      tagColor: 'success',
      options: [
        { value: SimType.PREPAID, label: 'เติมเงิน' },
        { value: SimType.POSTPAID, label: 'รายเดือน' },
        { value: SimType.YEARLY, label: 'รายปี' },
        { value: SimType.CORPORATE, label: 'องค์กร' }
      ]
    },
    {
      key: 'status',
      label: 'สถานะ',
      type: 'select',
      tagColor: 'accent',
      options: [
        // OBS-S544-001 (filter side): add migrated statuses so users can filter existing data
        // (all live SIMs are AVAILABLE/IN_USE). ACTIVE kept as deprecated backward-compat.
        { value: SimStatus.AVAILABLE, label: 'พร้อมใช้งาน' },
        { value: SimStatus.IN_USE, label: 'กำลังใช้งาน' },
        { value: SimStatus.INACTIVE, label: 'ไม่ใช้งาน' },
        { value: SimStatus.SUSPENDED, label: 'ระงับชั่วคราว' },
        { value: SimStatus.EXPIRED, label: 'หมดอายุ' },
        // ใบงานเตรียมเครื่อง (PO 2026-09-08): ต้องกรองหาซิมที่ถูกกันไว้ได้
        // ไม่งั้นซิมหายจากทุกตัวกรองโดยไม่มีใครรู้ว่าไปอยู่ใบไหน
        { value: SimStatus.RESERVED, label: 'จองไว้ (ใบงานเตรียมเครื่อง)' },
        // ยืมซิม (23 ก.ย. 2026): กรองหาซิมที่ถูกยืมไปได้
        { value: SimStatus.ISSUED, label: 'เบิกไปใช้ภายใน' },
        { value: SimStatus.ACTIVE, label: 'ใช้งาน (เดิม)' }
      ]
    },
    {
      key: 'expiring',
      label: 'ใกล้หมดอายุ',
      type: 'checkbox',
      tagColor: 'warning',
      placeholder: 'แสดงเฉพาะซิมที่ใกล้หมดอายุ'
    }
  ];

  // ค่า filter values สำหรับ SearchFilterCard
  const filterValues = {
    operator: filterOperator,
    sim_type: filterType,
    status: filterStatus,
    expiring: showExpiringOnly
  };

  // ฟังก์ชันจัดการการเปลี่ยนแปลง filter
  const handleFilterChange = (key: string, value: any) => {
    switch (key) {
      case 'operator':
        setFilterOperator(value);
        break;
      case 'sim_type':
        setFilterType(value);
        break;
      case 'status':
        setFilterStatus(value);
        break;
      case 'expiring':
        setShowExpiringOnly(value);
        break;
    }
    setCurrentPage(1);
  };

  // Render page header
  const renderPageHeader = () => {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <RiSimCard2Line className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-secondary-900 leading-tight">ซิมการ์ด</h1>
            <p className="text-xs text-secondary-500 mt-0.5">จัดการข้อมูลซิมการ์ดทั้งหมด</p>
          </div>
        </div>
        {totalItems > 0 && (
          <span className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 rounded-lg bg-secondary-100 text-secondary-600 text-xs font-medium self-start sm:self-auto">
            {totalItems.toLocaleString()} รายการ
          </span>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="w-full">
        {/* Page Header */}
          {renderPageHeader()}

          <ListLoadErrorBanner error={simsError} entityLabel="ซิม" onRetry={() => refetch()} />

          {/* Action Buttons Row */}
          <ActionButtonsRow
            onAdd={canCreate('sims') ? handleAdd : undefined}
            addLabel="เพิ่มข้อมูล"
            onRefresh={handleRefresh}
            refreshLabel="รีเฟรช"
            refreshLoading={simsFetching}
            onExport={canExport('sims') ? handleExportExcel : undefined}
            exportLabel="ส่งออก Excel"
            exportDisabled={sims.length === 0}
            showAdd={true}
            showRefresh={true}
            showExport={true}
          />

          {/* Search & Filter Card */}
          <SearchFilterCard
            searchTerm={searchTerm}
            onSearchChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            searchPlaceholder="ค้นหารหัสซิม/เบอร์โทรศัพท์..."
            filters={filters}
            filterValues={filterValues}
            onFilterChange={handleFilterChange}
            title="ค้นหาและกรองข้อมูล"
            showTitle={true}
            showFilterTags={true}
            showClearButton={true}
          />

          {/* Bulk Actions Bar */}
          {canDelete('sims') && (
            <BulkActionsBar
              selectedCount={selectedSims.length}
              onBulkDelete={handleBulkDelete}
              onClearSelection={() => setSelectedSims([])}
              loading={bulkLoading}
              deleteLabel="ลบที่เลือก"
            />
          )}

          {/* Table */}
          <Table
            columns={canDelete('sims') ? columns : columns.filter(c => c.key !== 'select')}  /* KD3 S2122 (BUG-S2122-004): ไม่มีสิทธิ์ลบ = ไม่มีช่องติ๊ก (ติ๊กแล้วไม่มีอะไรให้ทำ) */
            data={sims}
            loading={loading}
            pagination={false}
            searchable={false}
            sortable={true}
            actions={false}
          />

          {/* Pagination */}
          {!loading && sims.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setCurrentPage(1);
              }}
              showPageSizeSelector={true}
              showSummary={true}
              className="mt-5"
            />
          )}


        {/* Loading Overlay for Export */}
        <LoadingOverlayModal
          isOpen={isExporting}
          icon="export"
          title="กำลังส่งออกข้อมูล"
          submessage="กรุณารอสักครู่ ระบบกำลังเตรียมไฟล์ Excel"
        />
      </div>
    </Layout>
  );
};

export default SimPage;